import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useConfigLimits,
  useLimitFilterOptions,
  useBaseLimits,
  useLimitModifiers,
  useMultiplierModifiers,
  useCreateLimitModifier,
  useRemoveLimitModifier,
  useSyncLimitsToBQ,
  useMultipliers,
  useCreateMultiplierModifier,
  useRemoveMultiplierModifier,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { useHomebaseModal } from '../components/HomebaseModal';
import './LimitsConfigPage.css';

const BASE_FILTER_FIELDS = ['sport', 'market', 'limit_type', 'scope'];
const MULT_DIMENSIONS = ['user_tier', 'line_game_status', 'leg_count'];
const VIEWER_FILTER_FIELDS = ['sport', 'limit_type', 'scope', 'user_tier', 'line_game_status', 'market'];

const EMPTY_MULT_FORM = {
  limit_type: '', dimension: 'line_game_status', dimension_value: '',
  sport: '', market: '', multiplier: '', expiry_ts: '', note: '',
};

const EMPTY_ADD_LIMIT = {
  scope: '', sport: '', market: '', season_type: '', player_id: '',
  line_type: '', limit_type: '', promo_type: '', override_value_usd: '', note: '',
};
const ADD_LIMIT_IDENTITY = ['scope', 'sport', 'market', 'season_type', 'player_id', 'line_type', 'limit_type', 'promo_type'];

const BASE_IDENTITY = ['scope', 'sport', 'market', 'season_type', 'player_id', 'line_type', 'limit_type', 'promo_type'];

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return err?.message || 'Request failed';
}
function usd(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toLocaleString()}`;
}
function cell(v) {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}
function ts(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}
const anySet = (obj) => Object.values(obj).some((v) => String(v || '').trim() !== '');

// Retool app where limits are reviewed/enforced downstream (opens in a new tab).
const RETOOL_LIMITS_URL =
  'https://sleeperapp.retool.com/apps/279841b4-76ff-11f0-b1fe-b79457e7ea86/Picks/Limits%20Data/page1';

// Base-grain identity of a limit_modifiers row (matches the view's effective_raw join key).
const BASE_MOD_KEY = ['scope', 'sport', 'market', 'season_type', 'player_id', 'line_type', 'limit_type', 'promo_type'];
// Identity of a limit_multiplier_modifiers row (matches effective_multipliers).
const MULT_MOD_KEY = ['limit_type', 'dimension', 'dimension_value', 'sport', 'market', 'user_tier'];
const keyOf = (row, fields) => fields.map((f) => (row[f] == null ? '' : String(row[f]))).join('');
const tsMs = (v) => {
  const t = v ? new Date(v).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
};
// The inline editors stamp the transition into the note, e.g. "base override ($500 → $40)" or
// "override (2.0× → 0.05×)". Pull the left ("was") side so the change log can show status quo.
const parseWasFromNote = (note) => {
  const m = note && String(note).match(/\(\s*(.+?)\s*→\s*.+?\)/);
  return m ? m[1].trim() : null;
};

/**
 * App-owned change log: normalize limit_modifiers + limit_multiplier_modifiers into the history
 * table's shape (one row per Homebase action). Active/Superseded/Expired is derived per identity —
 * within a key the newest row (by created_at) is Active unless expired; older rows are Superseded
 * (their effective_to = the next version's effective_from). This mirrors the fanned changelog's
 * SCD-2 semantics without the cartesian fan-out (which stays in BQ limits_changelog only).
 */
function buildAppChangeLog(baseMods, multMods, filter, activeOnly) {
  const now = Date.now();
  const rows = [];

  baseMods.forEach((m) => {
    rows.push({
      kind: 'base',
      _key: 'b' + keyOf(m, BASE_MOD_KEY),
      effective_from: m.effective_ts || m.created_at,
      created_ms: tsMs(m.created_at || m.effective_ts),
      expiry_ts: m.expiry_ts || null,
      scope: m.scope, sport: m.sport, market: m.market, limit_type: m.limit_type,
      user_tier: null, line_game_status: null, leg_count: null,
      value_display: usd(m.override_value_usd),
      is_new: (m.mode || 'modify') === 'add',
      note: m.note,
      created_by: m.created_by,
    });
  });

  multMods.forEach((m) => {
    const dim = m.dimension;
    rows.push({
      kind: 'mult',
      _key: 'm' + keyOf(m, MULT_MOD_KEY),
      effective_from: m.effective_ts || m.created_at,
      created_ms: tsMs(m.created_at || m.effective_ts),
      expiry_ts: m.expiry_ts || null,
      scope: null, sport: m.sport, market: m.market, limit_type: m.limit_type,
      user_tier: dim === 'user_tier' ? m.dimension_value : m.user_tier || null,
      line_game_status: dim === 'line_game_status' ? m.dimension_value : null,
      leg_count: dim === 'leg_count' ? m.dimension_value : null,
      value_display: `${m.multiplier}×`,
      is_new: false,
      note: m.note,
      created_by: m.created_by,
    });
  });

  // Per identity key, order oldest→newest so each row's effective_to = the next version's start.
  const byKey = {};
  rows.forEach((r) => { (byKey[r._key] || (byKey[r._key] = [])).push(r); });
  Object.values(byKey).forEach((group) => {
    group.sort((a, b) => a.created_ms - b.created_ms);
    group.forEach((r, i) => {
      const next = group[i + 1];
      const prev = group[i - 1];
      const expired = r.expiry_ts && tsMs(r.expiry_ts) <= now;
      if (next) {
        r.effective_to = next.effective_from;
        r.status = 'Superseded';
      } else if (expired) {
        r.effective_to = r.expiry_ts;
        r.status = 'Expired';
      } else {
        r.effective_to = null;
        r.status = 'Active';
      }
      // Status quo before this change: what the edit replaced (from its note), else the prior
      // version at this key. New limits (mode='add') had no prior value.
      r.was_display = parseWasFromNote(r.note) || (prev ? prev.value_display : null);
    });
  });

  // Narrow to the "Live limits" filter (only the fields an override row actually carries).
  const wanted = (k) => String(filter?.[k] || '').trim();
  const matchesFilter = (r) =>
    ['scope', 'sport', 'market', 'limit_type', 'user_tier', 'line_game_status'].every((k) => {
      const want = wanted(k);
      if (!want) return true;
      if (r[k] == null) return true; // wildcard on this axis (e.g. base overrides span all tiers)
      return String(r[k]) === want;
    });

  return rows
    .filter((r) => (activeOnly ? r.status === 'Active' : true))
    .filter(matchesFilter)
    .sort((a, b) => tsMs(b.effective_from) - tsMs(a.effective_from));
}

export default function LimitsConfigPage() {
  const { user } = useAuth();
  const { confirm, ModalLayer } = useHomebaseModal();

  const [baseFilter, setBaseFilter] = useState({ sport: '', market: '', limit_type: '', scope: '' });
  const [rowVals, setRowVals] = useState({});
  const [multFilter, setMultFilter] = useState({ limit_type: '', dimension: '', sport: '' });
  const [multRowVals, setMultRowVals] = useState({});
  const [multForm, setMultForm] = useState(EMPTY_MULT_FORM);
  const [addForm, setAddForm] = useState(EMPTY_ADD_LIMIT);
  const [viewerDraft, setViewerDraft] = useState({
    sport: '', limit_type: '', scope: '', user_tier: '', line_game_status: '', market: '',
  });
  const [viewerApplied, setViewerApplied] = useState(viewerDraft);
  const [changesActiveOnly, setChangesActiveOnly] = useState(false);
  const [flash, setFlash] = useState(null);

  const options = useLimitFilterOptions().data?.options || {};

  const hasBaseFilter = useMemo(() => anySet(baseFilter), [baseFilter]);
  const baseQuery = useBaseLimits(baseFilter, { enabled: hasBaseFilter });

  const hasMultFilter = useMemo(() => anySet(multFilter), [multFilter]);
  const multQuery = useMultipliers(multFilter, { enabled: hasMultFilter });

  const createMod = useCreateLimitModifier();
  const removeMod = useRemoveLimitModifier();
  const createMult = useCreateMultiplierModifier();
  const removeMult = useRemoveMultiplierModifier();
  const syncBQMut = useSyncLimitsToBQ();

  const hasViewerFilter = useMemo(() => anySet(viewerApplied), [viewerApplied]);
  const viewerQuery = useConfigLimits({ ...viewerApplied, limit: 1000 }, { enabled: hasViewerFilter });
  // Change history = app override log only (limit_modifiers + multiplier modifiers), one row per
  // Homebase action. The full fanned SCD-2 history stays in BQ limits_changelog (written on Sync).
  const limitModsQuery = useLimitModifiers();
  const multModsQuery = useMultiplierModifiers();

  const baseRows = useMemo(() => baseQuery.data?.rows || [], [baseQuery.data]);
  const multRows = useMemo(() => multQuery.data?.rows || [], [multQuery.data]);
  const viewerRows = useMemo(() => viewerQuery.data?.rows || [], [viewerQuery.data]);
  const changesLoading = limitModsQuery.isLoading || multModsQuery.isLoading;
  const changesError = limitModsQuery.error || multModsQuery.error;
  const changeRows = useMemo(
    () =>
      buildAppChangeLog(
        limitModsQuery.data?.rows || [],
        multModsQuery.data?.rows || [],
        viewerApplied,
        changesActiveOnly
      ),
    [limitModsQuery.data, multModsQuery.data, viewerApplied, changesActiveOnly]
  );
  const busy =
    createMod.isPending || removeMod.isPending || syncBQMut.isPending ||
    createMult.isPending || removeMult.isPending;

  // Point the "Live limits" viewer at a just-edited key so its fanned new values load immediately.
  const focusViewer = (row, keys) => {
    const next = { sport: '', limit_type: '', scope: '', user_tier: '', line_game_status: '', market: '' };
    let any = false;
    keys.forEach((k) => { if (row[k] != null && String(row[k]).trim() !== '') { next[k] = String(row[k]); any = true; } });
    if (!any) return;
    setViewerDraft(next);
    setViewerApplied(next);
  };

  const effBase = (r) => (r.override_value_usd != null ? r.override_value_usd : r.value_usd);
  const effMult = (r) => (r.override_multiplier != null ? r.override_multiplier : r.seed_multiplier);

  // ---- Base value overrides ----
  const setBaseOverride = async (r, i) => {
    const val = parseFloat(rowVals[i]);
    if (Number.isNaN(val)) { setFlash({ err: 'Enter a numeric dollar value' }); return; }
    // A row with no committed seed value exists only as an override on a grain that isn't in the
    // base table (e.g. season_type='pre'). The view's overlay branch only rewrites existing raw
    // rows, so such an edit must be mode='add' to be enforced (it rides the view's ADD branch).
    const isNew = r.value_usd == null;
    const p = { created_by: user?.email || 'homebase', override_value_usd: val };
    if (isNew) p.mode = 'add';
    BASE_IDENTITY.forEach((k) => {
      if (r[k] !== null && r[k] !== undefined && String(r[k]).trim() !== '') p[k] = String(r[k]);
    });
    if (typeof r.correlation === 'boolean') p.correlation = r.correlation;
    if (typeof r.same_player_correlation === 'boolean') p.same_player_correlation = r.same_player_correlation;
    p.note = isNew ? `new base limit (${usd(val)})` : `base override (${usd(r.value_usd)} → ${usd(val)})`;
    const label = [r.sport, r.market, r.limit_type, r.line_type].filter(Boolean).join(' / ') || 'wildcard';
    const ok = await confirm({
      title: isNew ? 'Set new base limit' : 'Set base limit',
      body: `Set base for ${label} to ${usd(val)}? Tier / in-game / leg multipliers scale on top. Sync to enforce.`,
      confirmLabel: 'Set value',
    });
    if (!ok) return;
    try {
      await createMod.mutateAsync(p);
      setRowVals((m) => { const n = { ...m }; delete n[i]; return n; });
      focusViewer(r, ['scope', 'sport', 'market', 'limit_type']);
      setFlash({ ok: 'Base override set — updated values shown in “Live limits” below. Sync to BQ, then review in Retool.' });
    } catch (e) { setFlash({ err: formatApiError(e) }); }
  };
  const resetBaseOverride = async (r) => {
    if (!r.override_modifier_id) return;
    const hasSeed = r.value_usd != null;
    const ok = await confirm({
      title: hasSeed ? 'Reset to seed value' : 'Remove override',
      body: hasSeed
        ? `Revert to the committed base value ${usd(r.value_usd)}?`
        : 'This limit exists only as an override — there is no committed base value to revert to, so the row will disappear. Remove it?',
      confirmLabel: hasSeed ? 'Reset' : 'Remove',
      danger: true,
    });
    if (!ok) return;
    try { await removeMod.mutateAsync(r.override_modifier_id); setFlash({ ok: 'Override removed. Remember to Sync.' }); }
    catch (e) { setFlash({ err: formatApiError(e) }); }
  };

  // ---- Multiplier overrides (inline over current multipliers) ----
  const setMultOverride = async (r, i) => {
    const val = parseFloat(multRowVals[i]);
    if (Number.isNaN(val)) { setFlash({ err: 'Enter a numeric multiplier' }); return; }
    const body = {
      limit_type: r.limit_type, dimension: r.dimension, dimension_value: r.dimension_value,
      multiplier: val, created_by: user?.email || 'homebase',
      note: `override (${r.seed_multiplier}× → ${val}×)`,
    };
    if (r.sport) body.sport = r.sport;
    if (r.market) body.market = r.market;
    if (r.user_tier) body.user_tier = r.user_tier;
    const label = [r.limit_type, r.dimension + '=' + r.dimension_value, r.sport].filter(Boolean).join(' / ');
    const ok = await confirm({ title: 'Set multiplier', body: `Set ${label} to ${val}×? Sync to enforce.`, confirmLabel: 'Set' });
    if (!ok) return;
    try {
      await createMult.mutateAsync(body);
      setMultRowVals((m) => { const n = { ...m }; delete n[i]; return n; });
      focusViewer(r, ['sport', 'market', 'limit_type']);
      setFlash({ ok: 'Multiplier override set — updated values shown in “Live limits” below. Sync to BQ, then review in Retool.' });
    } catch (e) { setFlash({ err: formatApiError(e) }); }
  };
  const resetMultOverride = async (r) => {
    if (!r.override_modifier_id) return;
    const ok = await confirm({ title: 'Reset multiplier', body: `Revert to the seed multiplier ${r.seed_multiplier}×?`, confirmLabel: 'Reset', danger: true });
    if (!ok) return;
    try { await removeMult.mutateAsync(r.override_modifier_id); setFlash({ ok: 'Multiplier override removed. Remember to Sync.' }); }
    catch (e) { setFlash({ err: formatApiError(e) }); }
  };

  // ---- Add a brand-new base limit (mode='add') ----
  // Picking a Limit type auto-fills Scope with the scope that limit type conventionally uses
  // (data-driven from filter-options); Scope stays editable for the rare exception.
  const scopeByLimitType = options.scope_by_limit_type || {};
  const onAddField = (key, value) =>
    setAddForm((f) => {
      const next = { ...f, [key]: value };
      if (key === 'limit_type') {
        const auto = (scopeByLimitType[value.trim()] || [])[0];
        if (auto) next.scope = auto;
      }
      return next;
    });
  const addNewLimit = async () => {
    const scope = addForm.scope.trim();
    const limit_type = addForm.limit_type.trim();
    const val = parseFloat(addForm.override_value_usd);
    if (!scope || !limit_type) { setFlash({ err: 'Scope and limit type are required for a new limit' }); return; }
    if (Number.isNaN(val)) { setFlash({ err: 'Enter a numeric dollar value' }); return; }
    const body = { mode: 'add', override_value_usd: val, created_by: user?.email || 'homebase', note: addForm.note.trim() || undefined };
    ADD_LIMIT_IDENTITY.forEach((k) => { if (addForm[k].trim()) body[k] = addForm[k].trim(); });
    const label = [scope, addForm.sport, addForm.market, limit_type].filter(Boolean).join(' / ');
    const ok = await confirm({
      title: 'Add new base limit',
      body: `Create ${label} = ${usd(val)} as a NEW base limit? Tier / in-game / leg multipliers apply on top. Sync to enforce.`,
      confirmLabel: 'Add limit',
    });
    if (!ok) return;
    try {
      await createMod.mutateAsync(body);
      focusViewer({ scope, sport: addForm.sport.trim(), market: addForm.market.trim(), limit_type }, ['scope', 'sport', 'market', 'limit_type']);
      setAddForm(EMPTY_ADD_LIMIT);
      setFlash({ ok: 'New base limit added — see it in “Live limits” below. Sync to BQ, then review in Retool.' });
    } catch (e) { setFlash({ err: formatApiError(e) }); }
  };

  const onMultField = (key, value) => setMultForm((f) => ({ ...f, [key]: value }));
  const addMultiplier = async () => {
    const limit_type = multForm.limit_type.trim();
    const dimension_value = multForm.dimension_value.trim();
    const mult = parseFloat(multForm.multiplier);
    if (!limit_type || !dimension_value) { setFlash({ err: 'Limit type and dimension value are required' }); return; }
    if (Number.isNaN(mult)) { setFlash({ err: 'Multiplier must be a number' }); return; }
    const body = {
      limit_type, dimension: multForm.dimension, dimension_value, multiplier: mult,
      sport: multForm.sport.trim() || undefined, market: multForm.market.trim() || undefined,
      note: multForm.note.trim() || undefined, created_by: user?.email || 'homebase',
    };
    if (multForm.expiry_ts.trim()) body.expiry_ts = new Date(multForm.expiry_ts).toISOString();
    const ok = await confirm({ title: 'Add multiplier override', body: `Set ${multForm.dimension}=${dimension_value} to ${mult}× for ${limit_type}${body.sport ? ' / ' + body.sport : ''}. Sync to enforce.`, confirmLabel: 'Add' });
    if (!ok) return;
    try { await createMult.mutateAsync(body); setMultForm(EMPTY_MULT_FORM); setFlash({ ok: 'Multiplier override added. Remember to Sync.' }); }
    catch (e) { setFlash({ err: formatApiError(e) }); }
  };

  // ---- Viewer + sync ----
  const setViewerFilter = (k, v) => { const next = { ...viewerDraft, [k]: v }; setViewerDraft(next); setViewerApplied(next); };
  const clearViewer = () => {
    const blank = { sport: '', limit_type: '', scope: '', user_tier: '', line_game_status: '', market: '' };
    setViewerDraft(blank); setViewerApplied(blank);
  };
  const syncToBQ = async () => {
    try {
      const res = await syncBQMut.mutateAsync();
      setFlash({ ok: `Synced to BQ — ${res?.inserted ?? 0} new history row(s) recorded in limits_changelog.` });
    } catch (e) { setFlash({ err: formatApiError(e) }); }
  };
  return (
    <div className="limits-page">
      <ModalLayer />
      <div className="page-header">
        <div>
          <h1>Limits</h1>
          <p className="subtle">
            Edit <strong>base limits</strong> and <strong>multipliers</strong> via overrides (seed tables never
            touched). Overrides show instantly in “Live limits”. <strong>Sync to BQ</strong> records them in the
            limits_changelog history; <strong>Sync to Retool</strong> opens the Retool app where limits are reviewed/enforced.
          </p>
        </div>
        <div className="limits-header-actions">
          <button type="button" className="button" onClick={() => { baseQuery.refetch(); multQuery.refetch(); viewerQuery.refetch(); limitModsQuery.refetch(); multModsQuery.refetch(); }}>Refresh</button>
          <button type="button" className="button" onClick={syncToBQ} disabled={busy}>Sync to BQ</button>
          <Link to="/manual/limits/table" className="button">View config_limits table</Link>
          <a className="button button-primary" href={RETOOL_LIMITS_URL} target="_blank" rel="noopener noreferrer">Sync to Retool</a>
        </div>
      </div>

      {flash && (
        <div className={`card limits-flash${flash.err ? ' limits-flash--err' : ''}`}>{flash.err || flash.ok}</div>
      )}

      {/* Card 1: base limits (value overrides) */}
      <div className="card limits-card--base">
        <div className="limits-card-head">
          <h2>Base limits</h2>
          <span className="subtle">{!hasBaseFilter ? 'search — pick a filter to load' : `${baseRows.length} row(s) · set a base value; multipliers scale on top`}</span>
        </div>
        <div className="limits-filter-bar">
          {BASE_FILTER_FIELDS.map((k) => (
            <select key={k} className="filter-select" value={baseFilter[k]} onChange={(e) => setBaseFilter((f) => ({ ...f, [k]: e.target.value }))}>
              <option value="">{k}: any</option>
              {(options[k] || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ))}
        </div>
        <div className="table-container">
          <table className="table limits-table">
            <thead>
              <tr><th>Scope</th><th>Sport</th><th>Market</th><th>Limit</th><th>Line</th><th>Season</th><th>Player</th><th>Promo</th><th>Value</th><th>New value</th><th></th></tr>
            </thead>
            <tbody>
              {!hasBaseFilter ? (
                <tr><td colSpan={11} className="subtle">Search base limits — pick a filter above (e.g. Sport = nba) to load.</td></tr>
              ) : baseQuery.isLoading ? (
                <tr><td colSpan={11} className="subtle">Loading…</td></tr>
              ) : baseQuery.isError ? (
                <tr><td colSpan={11} className="limits-cell-error">Failed to load base limits: {formatApiError(baseQuery.error)}</td></tr>
              ) : baseRows.length === 0 ? (
                <tr><td colSpan={11} className="subtle">No base limits for these filters.</td></tr>
              ) : (
                baseRows.map((r, i) => (
                  <tr key={i} className={r.override_value_usd != null ? 'limits-row--modified' : ''}>
                    <td>{cell(r.scope)}</td><td>{cell(r.sport)}</td><td>{cell(r.market)}</td><td>{cell(r.limit_type)}</td>
                    <td>{cell(r.line_type)}</td><td>{cell(r.season_type)}</td><td>{cell(r.player_id)}</td><td>{cell(r.promo_type)}</td>
                    <td className="mono">{r.override_value_usd != null ? <span><strong>{usd(r.override_value_usd)}</strong>{r.value_usd != null ? <span className="subtle"> (was {usd(r.value_usd)})</span> : <span className="subtle"> (new)</span>}</span> : usd(r.value_usd)}</td>
                    <td className="limits-newval-cell">
                      <div className="limits-newval">
                        <input className="filter-input limits-newval-input" placeholder={usd(effBase(r))} value={rowVals[i] ?? ''} onChange={(e) => setRowVals((m) => ({ ...m, [i]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && setBaseOverride(r, i)} />
                        <button type="button" className="button button-small" onClick={() => setBaseOverride(r, i)} disabled={busy || !(rowVals[i] ?? '').trim()}>Set</button>
                      </div>
                    </td>
                    <td>{r.override_modifier_id && <button type="button" className="button button-small button-danger" onClick={() => resetBaseOverride(r)} disabled={busy}>Reset</button>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <details className="limits-add-mult">
          <summary className="subtle">Add a brand-new base limit (one that doesn't exist in the base table yet)</summary>
          <div className="limits-form">
            {/* Shared option lists: every field is a dropdown but stays free-typeable, so a
                brand-new sport/market (or scope/limit type) can still be entered. */}
            <datalist id="add-limit-types">{(options.limit_type || []).map((v) => <option key={v} value={v} />)}</datalist>
            <datalist id="add-scopes">{(options.scope || []).map((v) => <option key={v} value={v} />)}</datalist>
            <datalist id="add-sports">{(options.sport || []).map((v) => <option key={v} value={v} />)}</datalist>
            <datalist id="add-markets">{(options.market || []).map((v) => <option key={v} value={v} />)}</datalist>
            {/* Limit type first — picking it auto-fills Scope. */}
            <label className="limits-form-field"><span>Limit type *</span>
              <input className="filter-input" list="add-limit-types" placeholder="stake / market_liability / …" value={addForm.limit_type} onChange={(e) => onAddField('limit_type', e.target.value)} /></label>
            <label className="limits-form-field"><span>Scope * <span className="subtle">(auto-filled)</span></span>
              <input className="filter-input" list="add-scopes" placeholder="pick a limit type first" value={addForm.scope} onChange={(e) => onAddField('scope', e.target.value)} /></label>
            <label className="limits-form-field"><span>Value (USD) *</span>
              <input className="filter-input" placeholder="e.g. 5000" value={addForm.override_value_usd} onChange={(e) => onAddField('override_value_usd', e.target.value)} /></label>
            <label className="limits-form-field"><span>Sport</span>
              <input className="filter-input" list="add-sports" placeholder="any (or type new)" value={addForm.sport} onChange={(e) => onAddField('sport', e.target.value)} /></label>
            <label className="limits-form-field"><span>Market</span>
              <input className="filter-input" list="add-markets" placeholder="any (or type new)" value={addForm.market} onChange={(e) => onAddField('market', e.target.value)} /></label>
            <label className="limits-form-field"><span>Line type</span>
              <input className="filter-input" placeholder="any" value={addForm.line_type} onChange={(e) => onAddField('line_type', e.target.value)} /></label>
            <label className="limits-form-field"><span>Season type</span>
              <input className="filter-input" placeholder="any" value={addForm.season_type} onChange={(e) => onAddField('season_type', e.target.value)} /></label>
            <label className="limits-form-field"><span>Promo type</span>
              <input className="filter-input" placeholder="any" value={addForm.promo_type} onChange={(e) => onAddField('promo_type', e.target.value)} /></label>
            <div className="limits-form-actions">
              <button type="button" className="button button-primary" onClick={addNewLimit} disabled={busy}>Add new limit</button>
            </div>
          </div>
        </details>
      </div>

      {/* Card 2: multiplier overrides — search current multipliers + inline edit */}
      <div className="card limits-card--mult">
        <div className="limits-card-head">
          <h2>Multipliers</h2>
          <span className="subtle">{!hasMultFilter ? 'search — pick a filter to load' : `${multRows.length} row(s) · in-game / leg-count / tier scaling`}</span>
        </div>
        <div className="limits-filter-bar">
          <select className="filter-select" value={multFilter.limit_type} onChange={(e) => setMultFilter((f) => ({ ...f, limit_type: e.target.value }))}>
            <option value="">limit_type: any</option>
            {(options.limit_type || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="filter-select" value={multFilter.dimension} onChange={(e) => setMultFilter((f) => ({ ...f, dimension: e.target.value }))}>
            <option value="">dimension: any</option>
            {MULT_DIMENSIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="filter-select" value={multFilter.sport} onChange={(e) => setMultFilter((f) => ({ ...f, sport: e.target.value }))}>
            <option value="">sport: any</option>
            {(options.sport || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="table-container">
          <table className="table limits-table">
            <thead>
              <tr><th>Limit</th><th>Dimension</th><th>Value</th><th>Sport</th><th>Market</th><th>Tier</th><th>Multiplier</th><th>New multiplier</th><th></th></tr>
            </thead>
            <tbody>
              {!hasMultFilter ? (
                <tr><td colSpan={9} className="subtle">Search multipliers — pick a filter above (e.g. Dimension = line_game_status) to load.</td></tr>
              ) : multQuery.isLoading ? (
                <tr><td colSpan={9} className="subtle">Loading…</td></tr>
              ) : multQuery.isError ? (
                <tr><td colSpan={9} className="limits-cell-error">Failed to load multipliers: {formatApiError(multQuery.error)}</td></tr>
              ) : multRows.length === 0 ? (
                <tr><td colSpan={9} className="subtle">No multipliers for these filters.</td></tr>
              ) : (
                multRows.map((r, i) => (
                  <tr key={i} className={r.override_multiplier != null ? 'limits-row--modified' : ''}>
                    <td>{cell(r.limit_type)}</td><td>{cell(r.dimension)}</td><td>{cell(r.dimension_value)}</td>
                    <td>{cell(r.sport)}</td><td>{cell(r.market)}</td><td>{cell(r.user_tier)}</td>
                    <td className="mono">{r.override_multiplier != null ? <span><strong>{r.override_multiplier}×</strong> <span className="subtle">(was {r.seed_multiplier}×)</span></span> : `${r.seed_multiplier}×`}</td>
                    <td className="limits-newval-cell">
                      <div className="limits-newval">
                        <input className="filter-input limits-newval-input" placeholder={`${effMult(r)}×`} value={multRowVals[i] ?? ''} onChange={(e) => setMultRowVals((m) => ({ ...m, [i]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && setMultOverride(r, i)} />
                        <button type="button" className="button button-small" onClick={() => setMultOverride(r, i)} disabled={busy || !(multRowVals[i] ?? '').trim()}>Set</button>
                      </div>
                    </td>
                    <td>{r.override_modifier_id && <button type="button" className="button button-small button-danger" onClick={() => resetMultOverride(r)} disabled={busy}>Reset</button>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <details className="limits-add-mult">
          <summary className="subtle">Add a multiplier override for a new key (sport/market not shown above)</summary>
          <div className="limits-form">
            <label className="limits-form-field"><span>Limit type *</span>
              <input className="filter-input" placeholder="stake / liability / …" value={multForm.limit_type} onChange={(e) => onMultField('limit_type', e.target.value)} /></label>
            <label className="limits-form-field"><span>Dimension *</span>
              <select className="filter-select" value={multForm.dimension} onChange={(e) => onMultField('dimension', e.target.value)}>
                {MULT_DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select></label>
            <label className="limits-form-field"><span>Dimension value *</span>
              <input className="filter-input" placeholder={multForm.dimension === 'leg_count' ? '1 / 2' : multForm.dimension === 'user_tier' ? '2.0' : 'in_game'} value={multForm.dimension_value} onChange={(e) => onMultField('dimension_value', e.target.value)} /></label>
            <label className="limits-form-field"><span>Sport</span>
              <input className="filter-input" placeholder="any" value={multForm.sport} onChange={(e) => onMultField('sport', e.target.value)} /></label>
            <label className="limits-form-field"><span>Market</span>
              <input className="filter-input" placeholder="any" value={multForm.market} onChange={(e) => onMultField('market', e.target.value)} /></label>
            <label className="limits-form-field"><span>Multiplier *</span>
              <input className="filter-input" placeholder="e.g. 0.05" value={multForm.multiplier} onChange={(e) => onMultField('multiplier', e.target.value)} /></label>
            <div className="limits-form-actions">
              <button type="button" className="button button-primary" onClick={addMultiplier} disabled={busy}>Add multiplier override</button>
            </div>
          </div>
        </details>
      </div>

      {/* Card 3: fanned config_limits viewer (read-only search) */}
      <div className="card limits-card--limits">
        <div className="limits-card-head">
          <h2>Live limits (fanned)</h2>
          <span className="subtle">{!hasViewerFilter ? 'search — set a filter to load' : `${viewerQuery.data?.count ?? 0} row(s)${viewerApplied.user_tier ? '' : ' · tier: base + 2.0 default'}`}</span>
        </div>
        <div className="limits-filter-bar">
          {VIEWER_FILTER_FIELDS.map((k) => (
            <select key={k} className="filter-select" value={viewerDraft[k]} onChange={(e) => setViewerFilter(k, e.target.value)}>
              <option value="">{k}: any</option>
              {(options[k] || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ))}
          {hasViewerFilter && <button type="button" className="button button-small" onClick={clearViewer}>Clear</button>}
        </div>
        <div className="table-container">
          <table className="table limits-table">
            <thead>
              <tr><th>Scope</th><th>Sport</th><th>Market</th><th>Limit</th><th>Tier</th><th>Game</th><th>Line</th><th>Season</th><th>Player</th><th>Legs</th><th>Value</th></tr>
            </thead>
            <tbody>
              {!hasViewerFilter ? (
                <tr><td colSpan={11} className="subtle">Search limits — pick a filter above (e.g. Sport = nba) to load.</td></tr>
              ) : viewerQuery.isLoading ? (
                <tr><td colSpan={11} className="subtle">Loading…</td></tr>
              ) : viewerQuery.isError ? (
                <tr><td colSpan={11} className="limits-cell-error">Failed to load limits: {formatApiError(viewerQuery.error)}</td></tr>
              ) : viewerRows.length === 0 ? (
                <tr><td colSpan={11} className="subtle">No limits for these filters.</td></tr>
              ) : (
                viewerRows.map((r, i) => (
                  <tr key={i}>
                    <td>{cell(r.scope)}</td><td>{cell(r.sport)}</td><td>{cell(r.market)}</td><td>{cell(r.limit_type)}</td>
                    <td>{cell(r.user_tier)}</td><td>{cell(r.line_game_status)}</td><td>{cell(r.line_type)}</td>
                    <td>{cell(r.season_type)}</td><td>{cell(r.player_id)}</td><td>{cell(r.leg_count)}</td>
                    <td className="mono">{usd(r.value_usd)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card 4: change history — fanned limits_changelog (SCD-2), driven by the viewer filter */}
      <div className="card limits-card--changes">
        <div className="limits-card-head">
          <h2>Change history</h2>
          <span className="subtle">
            {`${changeRows.length} app change(s) · overrides made in Homebase, newest first`}
            {hasViewerFilter ? ' · filtered by “Live limits” above' : ''}
          </span>
        </div>
        <div className="limits-filter-bar">
          <span className="subtle">App-made overrides only — the full fanned history lives in BQ limits_changelog. Narrows to the “Live limits” filter above when one is set.</span>
          <label className="limits-form-field limits-changes-toggle">
            <input type="checkbox" checked={changesActiveOnly} onChange={(e) => setChangesActiveOnly(e.target.checked)} />
            <span>Currently-active only</span>
          </label>
        </div>
        <div className="table-container">
          <table className="table limits-table">
            <thead>
              <tr><th>Effective from</th><th>Effective to</th><th>Status</th><th>Scope</th><th>Sport</th><th>Market</th><th>Limit</th><th>Tier</th><th>Game</th><th>Legs</th><th>Value</th><th>By</th></tr>
            </thead>
            <tbody>
              {changesLoading ? (
                <tr><td colSpan={12} className="subtle">Loading…</td></tr>
              ) : changesError ? (
                <tr><td colSpan={12} className="limits-cell-error">Failed to load change history: {formatApiError(changesError)}</td></tr>
              ) : changeRows.length === 0 ? (
                <tr><td colSpan={12} className="subtle">No app-made overrides for these filters.</td></tr>
              ) : (
                changeRows.map((r, i) => (
                  <tr key={i} className={r.status === 'Active' ? 'limits-row--modified' : ''}>
                    <td className="mono">{ts(r.effective_from)}</td>
                    <td className="mono">{ts(r.effective_to)}</td>
                    <td>{r.status === 'Active' ? <strong>Active</strong> : r.status}</td>
                    <td>{cell(r.scope)}</td><td>{cell(r.sport)}</td><td>{cell(r.market)}</td><td>{cell(r.limit_type)}</td>
                    <td>{cell(r.user_tier)}</td><td>{cell(r.line_game_status)}</td><td>{cell(r.leg_count)}</td>
                    <td className="mono">{r.value_display}{r.was_display ? <span className="subtle"> (was {r.was_display})</span> : r.is_new ? <span className="subtle"> (new)</span> : null}</td><td>{cell(r.created_by)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
